import { DrawerClose, DrawerFooter } from "@/components/ui/drawer";
import { useOrdersWrite } from "@/hooks";
import { Order, OrderStatus, OrderType } from "@/models";
import { useEffect, useMemo, useState } from "react";
import { useToast } from "./ui/use-toast";
import { useMerchant } from "@/contexts";
import EthCrypto from "eth-crypto";
import { ethers } from "ethers";
import { ButtonType } from "./ui/button/button.type";
import { Button } from "./ui/button/button";
import { Skeleton } from "./ui/skeleton";
import { getMerchantUpi } from "@/helper/localstorage";
import { trackAmplitudeEvent } from "@/utils/amplitude";

interface AcceptOrderDrawerProps {
  order: Order;
  onClose?: () => void;
  isLoading: boolean;
}

const AcceptOrderDrawer = ({
  order,
  onClose,
  isLoading,
}: AcceptOrderDrawerProps) => {
  const { toast } = useToast();
  const { acceptOrder } = useOrdersWrite();
  const {
    merchantDetails,
    hasFetched: fetchedMerchantDetails,
    isLoading: fetchingMerchantDetails,
  } = useMerchant();
  const [encUpiId, setEncUpiId] = useState<string | null>(null);
  const [isAccepting, setIsAccepting] = useState(false);
  const [accepted, setAccepted] = useState(false);

  const hasAccepted = useMemo(
    () => accepted || order.status === OrderStatus.ACCEPTED,
    [accepted, order.status],
  );

  useEffect(() => {
    (async () => {
      const upi = await getMerchantUpi();
      if (upi) {
        setEncUpiId(upi);
      }
    })();
  }, []);

  const handleAcceptOrder = async () => {
    setIsAccepting(true);
    trackAmplitudeEvent("ORDER_ACCEPT_INITIATED");
    //To Do: merchant@upi should be replaced with actual merchant UPI ID
    const upiId = order.orderType === OrderType.BUY ? encUpiId : "";
    const _encUpiId =
      order.orderType === OrderType.BUY
        ? await EthCrypto.encryptWithPublicKey(order.pubkey, upiId as string)
        : "";
    const result = await acceptOrder(order.id, JSON.stringify(_encUpiId));
    //@ts-ignore
    if (result?.afterWait?.status === "success") {
      setAccepted(true);
      trackAmplitudeEvent("ORDER_ACCEPT_SUCCESS");
      toast({
        title: "Order Accepted",
        description: `Order ${order.id} status changed from PLACED to ACCEPTED.`,
      });
    } else {
      trackAmplitudeEvent("ORDER_ACCEPT_FAILED");
      toast({
        title: "Order wasn't Accepted",
        description: result.message,
        variant: "destructive",
      });
    }
    setIsAccepting(false);
  };

  return (
    <>
      <section className="mx-4 flex flex-col justify-center gap-2">
        <div className="flex flex-col justify-center gap-1">
          <label
            style={{
              fontWeight: "900",
            }}
            className={`font-satoshiBold w-fit rounded p-2 text-xs tracking-wide text-text-dark ${order.orderType === OrderType.BUY ? "bg-green-500" : "bg-yellow-500"}`}
            htmlFor="address">
            {order.orderType === OrderType.SELL ? "SEND" : "RECEIVE"}
          </label>
          <p className="text-2xl font-bold text-text-light">
            ₹{" "}
            {Math.round(Number(ethers.utils.formatUnits(order.fiatAmount, 6)))
              .toFixed(2)
              .toString()}
          </p>
        </div>
        <div className="flex flex-col justify-center gap-1">
          <label
            className="font-satoshiBold text-xs tracking-wide text-text-light"
            htmlFor="address">
            TO {order.orderType === OrderType.SELL ? "THIS" : "YOUR"} UPI ID
          </label>
          <div className="flex items-center justify-start gap-4">
            <p className="text-2xl font-bold text-text-light">
              {order.orderType === OrderType.BUY
                ? !fetchingMerchantDetails &&
                  fetchedMerchantDetails &&
                  merchantDetails
                  ? encUpiId
                  : ""
                : "Accept to view UPI"}
            </p>
          </div>
        </div>
      </section>
      <DrawerFooter>
        {isLoading || hasAccepted ? (
          <Skeleton className="h-[44px] w-full bg-bg-grey" />
        ) : (
          <>
            {!hasAccepted ? (
              <Button
                label={"Accept"}
                loading={isAccepting}
                onClick={handleAcceptOrder}
                type={ButtonType.primary}
                widthFull
              />
            ) : (
              "Order Accepted, you can close this now."
            )}
          </>
        )}
        <DrawerClose
          onClick={() => {
            onClose?.();
          }}>
          <div className="w-full rounded-md border-[1px] border-black bg-bg-secondary p-2 font-bold text-text-light">
            Close
          </div>
        </DrawerClose>
      </DrawerFooter>
    </>
  );
};

export default AcceptOrderDrawer;
